import { createApp } from 'vue';
import * as Sentry from '@sentry/vue';
import { createHead } from '@unhead/vue/client';
import { createAnnouncements } from '@/app/modules/announcements';
import { createRecentlyVisited } from '@/app/modules/recently-visited';
import { createI18n } from '@/app/modules/i18n';
import { createClarity } from '@/app/modules/clarity';
import { createOnboardingHints } from '@/app/modules/onboarding-hints';
import { createToaster, createConfirmDialog } from '@/ui-kit';
import { config } from '@/app/core/config';
import { createApiClient } from '@/app/core/api-client';
import { createRouter } from '@/app/core/router';
import { createStore } from '@/app/core/store';
import { createHeap } from '@/app/modules/heap';
import { createFeatureFlags } from '@/app/modules/feature-flags';
import { createEventBus } from '@/app/core/event-bus';
import App from '@/app/app.vue';
import '@/app/app.css';
import '@/ui-kit/styles/main.scss';

(async () => {
  const apiClient = createApiClient({
    baseUrl: config.apiUrl,
    keycloak: {
      url: config.keycloak.url,
      realm: config.keycloak.realm,
      clientId: config.keycloak.clientId,
      silentCheckSsoRedirectUri: config.keycloak.silentCheckSsoRedirectUri,
      logoutRedirectUri: `${window.location.origin}${config.baseUrl}`,
    },
  });

  await apiClient.init();

  const router = createRouter();
  const app = createApp(App)
    .use(createEventBus())
    .use(createStore())
    .use(apiClient)
    .use(router)
    .use(createI18n())
    .use(createHead())
    .use(
      createFeatureFlags({
        url: config.unleash.url,
        clientKey: config.unleash.clientKey,
        appName: config.unleash.appName,
        environment: config.environment,
        refreshInterval: config.unleash.refreshInterval,
      }),
    )
    .use(createToaster())
    .use(createConfirmDialog())
    .use(createRecentlyVisited({ router }))
    .use(createAnnouncements())
    .use(createOnboardingHints());

  if (config.clarity.projectId) {
    app.use(createClarity(config.clarity.projectId));
  }

  if (config.heap.enabled) {
    app.use(createHeap(config.heap.appId));
  }

  if (config.sentry.enabled) {
    Sentry.init({
      app,
      environment: config.environment,
      dsn: config.sentry.dsn,
      integrations: [
        Sentry.browserTracingIntegration({
          router,
        }),
      ],
      tracePropagationTargets: ['localhost', /^\//],
      tracesSampleRate: 1,
      tracingOptions: {
        trackComponents: true,
      },
      attachProps: true,
      attachStacktrace: true,
      logErrors: config.environment !== 'production',
    });
  }

  app.config.idPrefix = 'onebeat';

  app.config.errorHandler = (error) => {
    if (config.sentry.enabled) {
      Sentry.captureException(error);
    }

    console.error(error);
  };

  app.mount('#app');
})();
